import { APIScope, FeatureFlagKey, LocationScope } from '@swivl-mono/types';
import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { RouteShowIf } from '../../Components/ShowIf/RouteShowIf';
import { NavigationHelper } from '../../Helpers/Navigation/Navigation.Helper';
import { useHasPermission } from '../../Helpers/Permissions/useHasPermission';
import { AdminView } from '../../Views/Admin/Admin.View';
import { OnboardingView } from '../../Views/Admin/Onboarding/Onboarding.View';
import { DocumentIngestView } from '../../Views/Admin/DocumentIngest/DocumentIngest.View';
import { BulkImportPage } from '../../Views/Admin/DocumentIngest/BulkImportPage';
import { BillingView } from '../../Views/Admin/Billing/Billing.View';
import { FeatureFlagsAdminView } from '../../Views/Admin/FeatureFlags/FeatureFlags.View';
import { FMSCapabilitiesView } from '../../Views/Admin/FMSCapabilities/FMSCapabilities.View';
import { SequenceEventsView } from '../../Views/CRM/SequenceEvents/SequenceEvents.View';
import { SmsCampaignsView } from '../../Views/CRM/SmsCampaigns/SmsCampaigns.View';
import { TestCallerAdminView } from '../../Views/Admin/TestCaller/TestCaller.View';
import { SequenceTestRunAdminView } from '../../Views/Admin/SequenceTestRun/SequenceTestRun.View';
import { OutboundAllowlistAdminView } from '../../Views/Admin/OutboundAllowlist/OutboundAllowlist.View';
import { ServerDebugView } from '../../Views/Admin/ServerDebug/Admin.ServerDebug.View';
import { InviteView } from '../../Views/Login/Invites/InviteView';
import { LoginLayout } from '../../Views/Login/Layout/Login.Layout';
import { PropertiesView } from '../../Views/Properties/Properties.View';
import { PropertiesV3View } from '../../Views/Properties/PropertiesV3.View';
import { InteractionsSettingsView } from '../../Views/Settings/Interactions/Interactions.Settings.View';
import { MembersSettingsView } from '../../Views/Settings/Members/Members.Settings.View';
import { RolesSettingsView } from '../../Views/Settings/Roles/Roles.Settings.View';
import { SettingsView } from '../../Views/Settings/Settings.View';

/** `/crm/sequence-suppressions?...` → the Sequences page, same filters, Suppressions tab (ENG-3811). */
function SequenceSuppressionsRedirect(): React.ReactElement {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", "suppressions");
  return <Navigate to={{ pathname: "/crm/sequence-events", search: `?${params.toString()}` }} replace />;
}

import { WorksheetLayoutView } from '../../Components/Worksheets/Layout/Worksheet.Layout.View';
import { PolicyWorksheetsView } from '../../Views/Properties/PolicyWorksheets/PolicyWorksheetsView';
import { MenuView } from '../../Views/Menu/MenuView';
import { AdminVariablesEditor } from '../../Views/Admin/Variables/Admin.VariablesEditor';
import { MigrateToCortexV3View } from '../../Views/Admin/MigrateToCortexV3/MigrateToCortexV3.View';
import { PolicyNotificationsView } from '../../Views/Admin/PolicyNotifications/PolicyNotifications.View';
import { CortexWorksheetsData } from '../../Worksheets/Cortex/Cortex.Worksheets.Data';
import { DetailsV3WorksheetsData } from '../../Worksheets/DetailsV3/DetailsV3.Worksheets.Data';
import { LocationWorksheetsData } from '../../Worksheets/Details/Location.Worksheets.Data';
import { ReportsView } from '../../Worksheets/Reports/Reports.View';
import { PageLoadingView } from '../Layout/Loading/LoadingView';

import { BrandDetailsView } from '../../Views/Properties/Brands/Brand.Details.View';
import BrandsView from '../../Views/Properties/Brands/Brands.View';
import { EmbedDetailsView } from '../../Views/Properties/Embeds/Embed.Details.View';
import EmbedsView from '../../Views/Properties/Embeds/Embeds.View';

import React from 'react';

const ContactCenterV3View = React.lazy(() => import('../../Views/ContactCenterV3/ContactCenterV3View'));
const ConversationsV3Page = React.lazy(() => import('../../Views/ContactCenterV3/Conversations/ConversationsPage'));
const AgentDashboardPage = React.lazy(() => import('../../Views/ContactCenterV3/AgentDashboard/AgentDashboardPage'));
const AgentDashboardTVModePage = React.lazy(() => import('../../Views/ContactCenterV3/AgentDashboard/TVMode/TVModePage'));
const PulsePage = React.lazy(() => import('../../Views/Pulse/PulsePage'));

// Widget Builder (Snowy v4 — operator-facing config UI). See
// docs/snowy/widget/2026-05-20-widget-builder-b0-plan.md.
const WidgetsListView = React.lazy(() => import('../../Views/Widgets/Widgets.List.View'));
const WidgetDetailLayout = React.lazy(() => import('../../Views/Widgets/Widget.Detail.Layout'));
const BrandingAndCtaView = React.lazy(() => import('../../Views/Widgets/BrandingAndCta.View'));
const DisplayRulesView = React.lazy(() => import('../../Views/Widgets/DisplayRules.View'));
const CtasLibraryView = React.lazy(() => import('../../Views/Ctas/CtasLibrary.View'));
const CtaWizardView = React.lazy(() => import('../../Views/Ctas/CtaWizard.View'));
const InstallView = React.lazy(() => import('../../Views/Install/Install.View'));
const BrandsListView = React.lazy(() => import('../../Views/Brands/Brands.List.View'));
const BrandDetailView = React.lazy(() => import('../../Views/Brands/Brand.Detail.View'));
const ReservationsListView = React.lazy(() => import('../../Views/Reservations/Reservations.List.View'));

// Widget test harness in the Admin section (ENG-2991) — the v4 counterpart
// of the Embed Tester. Lazy so libs/widget-harness stays out of the main chunk.
const WidgetTesterAdmin = React.lazy(() => import('../../Views/Admin/WidgetTester/WidgetTester'));

// Policy Builder — the local-dev-only definition editor (design doc §8).
// import.meta.env.DEV is statically false in production builds, so this lazy
// chunk and its route below are dead-code-eliminated: deployed Studio contains
// no builder route and no builder code.
const PolicyBuilderView = import.meta.env.DEV ? React.lazy(() => import('../../Views/PolicyBuilder/PolicyBuilderView')) : null;

import EmbedTester from '../../Views/Admin/EmbedTester/EmbedTester';
import AgentSettingsView from '../../Views/ContactCenter/Conversations/Components/Agent/AgentSettingsView';
import ConversationsView from '../../Views/ContactCenter/Conversations/ConversationsView';
import AIConversationsView from '../../Views/AIConversations/AIConversationsView';
import AIStudioView from '../../Views/AIStudio/AIStudioView';
import ContactCenterView from '../../Views/ContactCenter/ContactCenterView';
import RecordingsView from '../../Views/ContactCenter/Recordings/RecordingsView';
import EditorWrapper from '../../Views/Quests/EditorWrapper';
import ReviewsView from '../../Views/Reviews/Reviews.View';
import AIAssistSettingsView from '../../Views/Settings/AIAssistSettings/AIAssistSettings.View';
import AnalyticsSettingsView from '../../Views/Settings/Analytics/Analytics.Settings.View';
import AppSettingsView from '../../Views/Settings/Apps/AppsView';
import BotPromptsSettingView from '../../Views/Settings/BotPrompts/BotPromptsSettingView';
import BrainsSettingView from '../../Views/Settings/Brains/BrainsSettingView';
import EscalationsView from '../../Views/Settings/Escalations/EscalationsView';
import FMSDetailsView from '../../Views/Settings/FMSIntegrations/FMSDetails/FMSDetailsView';
import FMSIntegrationsView from '../../Views/Settings/FMSIntegrations/FMSIntegrationsView';
import UserJourneysView from '../../Views/Workflows/UserJourneysView';
import { NavBarLayout } from '../Layout/NavBar/NavBarView';

import ErrorView from '../../Components/ErrorView/ErrorView';
import { RouterWorksheetData } from '../../Components/Worksheets/Types/Worksheet.Interface';
import { isFeatureEnabled } from '../../Helpers/FeatureFlags/isFeatureEnabled';
import { isPulseEnabled } from '../../Helpers/FeatureFlags/isPulseEnabled';
import { isSequencesConfigPageVisible } from '../../Helpers/FeatureFlags/isSequencesConfigPageVisible';
import { LegacyLandingRedirect } from './LegacyLandingRedirect';
import { Member } from '../../Models/Member/Member.Current';
import { UsersModel } from '../../Models/Users/Users.Model';
import { AnalyticsPlaygroundView } from '../../Views/Admin/Analytics/AnalyticsPlayground.View';
import { NLPCachesView } from '../../Views/Admin/NLPCaches/Admin.NLPCaches.View';
import { NLPComparisonView } from '../../Views/Admin/NLPCaches/Admin.NLPComparison.View';
import { CRMView as OldCRMView } from '../../Views/Analytics/CRM/CRM.View';
import BrainsView from '../../Views/Brains/BrainsView';
import TranscriptView from '../../Views/ContactCenter/Conversations/Transcript/TranscriptView';
import NotificationsView from '../../Views/Notifications/Notifications.View';
import { SpotView } from '../../Views/Properties/Spot/Spot.View';
import { SpotV3View } from '../../Views/Properties/SpotV3/SpotV3.View';
import { SequenceTypePanel } from '../../Views/Properties/SequencesV3/SequenceTypePanel';
import { SequencesV3View } from '../../Views/Properties/SequencesV3/SequencesV3.View';
import EscalationHistoryView from '../../Views/Settings/EscalationHistory/EscalationHistoryView';
import { SequenceDetailsView } from '../../Views/Settings/Sequences/Details/SequenceDetails.View';
import { SequenceRecordDetails } from '../../Views/Settings/Sequences/Record/SequenceRecordDetails';
import { SequenceSettingsView } from '../../Views/Settings/Sequences/SequencesSettings.View';
import { RouterRedirect } from './Helpers/RouterRedirect';
import { WhatsNewView } from '../../Views/Dashboard/WhatsNew/WhatsNew.View';
import CRMView from '../../Views/CRM/CRMView';
import { TicketingPage } from '../../Views/Ticketing/Ticketing.Page';
import { TicketTemplatesPage } from '../../Views/Ticketing/TicketTemplates/TicketTemplatesPage';
import TicketAccessView from '../../Views/TicketAccess/TicketAccessView';
import TranscriptAccessView from '../../Views/TranscriptAccess/TranscriptAccessView';

navigator.serviceWorker.addEventListener("message", (ev) => {
  console.log('sw- ev :', ev);
  if (ev.data?.type === "NOTIF_OPENED") {
    // e.g., bring a modal forward, select the queue item, start polling, etc.
    // store.dispatch(uiActions.focusEscalation(ev.data.data?.id));
    if (ev.data?.url) {
      // Broadcast an event so reactrouter can listen and navigate
      const detail = { url: ev.data.url, tag: ev.data.payload?.tag };
      NavigationHelper.navFunction?.(ev.data.url);
      if (ev.data.payload?.tag && ev.data.payload.tag.startsWith("escalation:")) {
        UsersModel.acknowledgeEscalationById(parseInt(ev.data.payload.tag.split(":")[1], 10));
      }
      console.log('sw- NOTIF_OPENED url:', ev.data.url, ev.data.payload, detail);

    }
  }
});

function WrapInScope(element: any, scopes?: APIScope) {
  if (scopes) {
    return <RouteShowIf scopes={[scopes]}>{element}</RouteShowIf>;
  } else {
    return element;
  }
}

function PropertiesIndexRedirect() {
  const canViewLocationDetails = useHasPermission(APIScope.LOCATION_DETAILS_READ);
  return <Navigate to={canViewLocationDetails ? "/properties/page/details/info" : "/properties/page/spot"} replace />;
}

// `/` is Pulse where it's on for the viewer (ENG-3237).
function DefaultLandingRedirect() {
  if (isPulseEnabled()) {
    return (
      <Suspense fallback={<PageLoadingView />}>
        <PulsePage />
      </Suspense>
    );
  }
  return <LegacyLandingRedirect />;
}

function RouteWorksheetForData(data: RouterWorksheetData[]) {
  const isStaff = Member.isSwivlStaff;
  return data
    .filter((item) => !item.swivlStaffOnly || isStaff)
    .map((item, index) => {
    const to = item.to;

    if (item.children && item.children.length > 0) {
      return (
        <Route key={'parent' + to} path={to} element={<Outlet />}> {RouteWorksheetForData(item.children)}
        </Route>
      );
    } // We need to return a react fragment to avoid a warning
    return (
      <React.Fragment key={to + 'parent'}>
        <Route key={to + 'default'} path={to} element={<Suspense fallback={<PageLoadingView />}>{WrapInScope(item.element, item.apiScope)}</Suspense>} />
        <Route key={to + 'action'} path={to + '/:action'} element={<Suspense fallback={<PageLoadingView />}>{WrapInScope(item.element, item.apiScope)}</Suspense>} />
      </React.Fragment>
    );
  });
}

export default function RouterLoggedIn() {
  const navigate = useNavigate();
  // Same double guard as NavDrawer: only an org on Snowy can hide its legacy nav (ENG-3490).
  const legacyNavHidden = isFeatureEnabled(FeatureFlagKey.HideLegacyNav) && isFeatureEnabled(FeatureFlagKey.Snowy);
  useEffect(() => {
    NavigationHelper.navFunction = navigate as any;
  }, [navigate]);

  return (
    <>
      <Routes>
        <Route path="/select-org" element={<Navigate to={'/'} />} />

        {/* invite routes */}
        <Route path="/invite" element={<LoginLayout />}>
          <Route path="/invite/:inviteUUID" element={<InviteView />} />
        </Route>

        {/* All authenticated routes with navbar */}
        <Route path="/" element={<NavBarLayout />}>

          {/* admin routes */}
          <Route path="/admin" element={<RouteShowIf scopes={[APIScope.SUPER_ADMIN_ONLY]}><AdminView /></RouteShowIf>}>
            <Route path="embed" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}><EmbedTester /></Suspense>} />
            <Route path="embed/:orgId" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <EmbedTester />     </Suspense>} />
            <Route path="embed/:orgId/:embedId" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <EmbedTester />     </Suspense>} />
            <Route path="widget" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}><WidgetTesterAdmin /></Suspense>} />
            <Route path="variables" element={<AdminVariablesEditor />} />
            <Route path="feature-flags" element={<FeatureFlagsAdminView />} />
            <Route path="fms-capabilities" element={<RouteShowIf scopes={[APIScope.SWIVL_STAFF_ONLY]}><FMSCapabilitiesView /></RouteShowIf>} />
            <Route path="test-caller" element={<TestCallerAdminView />} />
            <Route
              path="sequence-test-run"
              element={
                <RouteShowIf scopes={[APIScope.SWIVL_ADMIN_ONLY]}>
                  <SequenceTestRunAdminView />
                </RouteShowIf>
              }
            />
            <Route
              path="outbound-allowlist"
              element={
                <RouteShowIf scopes={[APIScope.SWIVL_ADMIN_ONLY]}>
                  <OutboundAllowlistAdminView />
                </RouteShowIf>
              }
            />
            <Route path="server-debug" element={<ServerDebugView />} />
            <Route path="onboarding" element={<OnboardingView />} />
            <Route path="billing" element={<BillingView />} />
            <Route path="analytics" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <AnalyticsPlaygroundView />     </Suspense>} />
            <Route path="nlp-caches" element={<NLPCachesView />} />
            <Route path="nlp-comparison" element={<NLPComparisonView />} />
            <Route path="cortex-v3-migrate" element={<MigrateToCortexV3View />} />
            <Route path="policy-notifications" element={<PolicyNotificationsView />} />
            <Route path="*" element={<Navigate to="/admin/server-debug" />} />
          </Route>

          {/* Policy Builder — local dev only (see the dev-gated lazy declaration above) */}
          {import.meta.env.DEV && PolicyBuilderView !== null && (
            <Route path="/policy-builder" element={<Suspense fallback={<PageLoadingView />}><PolicyBuilderView /></Suspense>} />
          )}

          {/* settings routes */}
          <Route path="agent/settings" element={<Suspense fallback={<PageLoadingView />}><AgentSettingsView /></Suspense>} />
          <Route path="/settings" element={<SettingsView />}>
            <Route path="ai-assist" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AIAssistSettingsView />       </Suspense>     </RouteShowIf>} />

            <Route path="apps" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="apps/:appType" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="apps/:appType/:appId" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <AppSettingsView />       </Suspense>     </RouteShowIf>} />
            <Route path="bot-prompts" element={<Navigate to="/ai-studio/prompts" replace />} />
            <Route path="bot-prompts/:slug" element={<RouterRedirect to="/ai-studio/prompts/:slug" replace />} />
            <Route path="brains" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <BrainsSettingView />     </RouteShowIf>} />
            <Route path="roles" element={<RouteShowIf scopes={[APIScope.ROLES_VIEW]}>       <RolesSettingsView />     </RouteShowIf>} />
            <Route path="roles/:roleSlug" element={<RouteShowIf scopes={[APIScope.ROLES_VIEW]}>       <RolesSettingsView />     </RouteShowIf>} />
            <Route path="members" element={<RouteShowIf scopes={[APIScope.MEMBERS_GET]}>       <MembersSettingsView />     </RouteShowIf>} />
            <Route path="members/:memberOrInvite/:objectId" element={<RouteShowIf scopes={[APIScope.MEMBERS_GET]}>       <MembersSettingsView />     </RouteShowIf>} />
            <Route path="ticket-templates" element={<RouteShowIf scopes={[APIScope.TICKETS_EDIT]}>       <TicketTemplatesPage />     </RouteShowIf>} />
            <Route path="*" element={<Navigate to="/settings/analytics" replace />} />
            <Route path="interactions" element={Member.isSwivlStaff ? <InteractionsSettingsView /> : <Navigate to="/settings" replace />} />
            <Route path="interactions/:startDate/:endDate" element={Member.isSwivlStaff ? <InteractionsSettingsView /> : <Navigate to="/settings" replace />} />
            {/* Legacy escalation, sequence and workflow pages hide with the legacy nav (ENG-3490): link gone in Settings, URL lands on the Settings landing page (Analytics). */}
            <Route path="escalations" element={legacyNavHidden ? <Navigate to="/settings/analytics" replace /> : <RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>              <EscalationsView />     </RouteShowIf>} />
            <Route path="escalation-history" element={legacyNavHidden ? <Navigate to="/settings/analytics" replace /> : <RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EscalationHistoryView />       </Suspense>      </RouteShowIf>} >
              <Route path=":escalationId" element={<RouteShowIf scopes={[APIScope.ESCALATIONS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EscalationHistoryView />       </Suspense>      </RouteShowIf>} />
            </Route>
            <Route path="analytics" element={<Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>       <AnalyticsSettingsView />     </Suspense>} />
            <Route path="document-ingest" element={isFeatureEnabled(FeatureFlagKey.Snowy) ? <RouteShowIf scopes={[APIScope.LOCATION_DETAILS_WRITE]}><DocumentIngestView /></RouteShowIf> : <Navigate to="/settings" replace />} />
            <Route path="document-ingest/bulk/:uploadId" element={isFeatureEnabled(FeatureFlagKey.Snowy) ? <RouteShowIf scopes={[APIScope.LOCATION_DETAILS_WRITE]}><BulkImportPage /></RouteShowIf> : <Navigate to="/settings" replace />} />
            <Route path="fms-integrations" element={<RouteShowIf scopes={[APIScope.FMS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <FMSIntegrationsView />       </Suspense>     </RouteShowIf>} />
            <Route path="sequences" element={legacyNavHidden ? <Navigate to="/settings/analytics" replace /> : <RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceSettingsView />       </Suspense>      </RouteShowIf>} >
              <Route path=":sequenceId" element={<RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceDetailsView />       </Suspense>      </RouteShowIf>} />
            </Route>
          </Route>

          {/* properties routes */}
          <Route path="properties" element={<RouteShowIf scopes={[APIScope.LOCATIONS_ALL_VIEW]}><PropertiesView /></RouteShowIf>}>
            {/* LOCATION ROUTES */}
            <Route path="group/:groupId/location/:locationId/page/details" element={<RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}><WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.LOCATION]} page={'details'} /></RouteShowIf>}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.LOCATION])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* GROUP ROUTES */}
            <Route path="group/:groupId/page/details" element={<RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}><WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.GROUP]} page={'details'} /></RouteShowIf>}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.GROUP])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* GROUP ORG */}
            <Route path="page/details" element={<RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}><WorksheetLayoutView showHeader={true} data={LocationWorksheetsData[LocationScope.ORG]} page={'details'} /></RouteShowIf>}>   {RouteWorksheetForData(LocationWorksheetsData[LocationScope.ORG])}   <Route path="*" element={<Navigate to="info" />} /> </Route>
            {/* LOCATION CORTEX ROUTES */}
            <Route
              path="group/:groupId/location/:locationId/page/cortex"
              element={
                <RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}>
                  <WorksheetLayoutView data={CortexWorksheetsData[LocationScope.LOCATION]} page={'cortex'} />
                </RouteShowIf>
              }
            >
              {RouteWorksheetForData(CortexWorksheetsData[LocationScope.LOCATION])}
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
            {/* GROUP CORTEX ROUTES */}
            <Route path="group/:groupId/page/cortex" element={<RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}><WorksheetLayoutView data={CortexWorksheetsData[LocationScope.GROUP]} page={'cortex'} /></RouteShowIf>}>   {RouteWorksheetForData(CortexWorksheetsData[LocationScope.GROUP])}   <Route path="*" element={<Navigate to="/" />} /> </Route>
            {/* ORG CORTEX ROUTES */}
            <Route
              path="page/cortex"
              element={
                <RouteShowIf scopes={[APIScope.LOCATION_DETAILS_READ]}>
                  <WorksheetLayoutView data={CortexWorksheetsData[LocationScope.ORG]} page={'cortex'} />
                </RouteShowIf>
              }
            >
              {RouteWorksheetForData(CortexWorksheetsData[LocationScope.ORG])}
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
            {/* ─── MENU ROUTES (Snowy buttons build, 2026-04-28) ──────────── */}
            <Route path="page/menu" element={<MenuView />} />
            <Route path="group/:groupId/page/menu" element={<MenuView />} />
            <Route path="group/:groupId/location/:locationId/page/menu" element={<MenuView />} />
            {/* GROUP CORTEX ORG */}

            <Route path="group/:groupId/location/:locationId/page/spot" element={<SpotView />} />
            {/* GROUP CORTEX ROUTES */}
            <Route path="group/:groupId/page/spot" element={<SpotView />} />
            {/* ORG CORTEX ROUTES */}
            <Route path="page/spot" element={<SpotView />} />

            {/* LOCATION REPORTS ROUTES */}
            <Route path="group/:groupId/location/:locationId/page/reports" element={<ReportsView />} />
            <Route path="group/:groupId/location/:locationId/page/reports/:folder/:dashboardId" element={<ReportsView />} />
            {/* GROUP REPORTS ROUTES */}
            <Route path="group/:groupId/page/reports" element={<ReportsView />} />
            <Route path="group/:groupId/page/reports/:folder/:dashboardId" element={<ReportsView />} />
            {/* ORG REPORTS ROUTES */}
            <Route path="page/reports" element={<ReportsView />} />
            <Route path="page/reports/:folder/:dashboardId" element={<ReportsView />} />

            <Route path="group/:groupId/page/*" element={<Navigate to="./details" />} />
            <Route path="group/:groupId/location/:locationId/page/*" element={<Navigate to="./details" />} />

            <Route path="page/brands" element={<BrandsView />}>
              <Route path=":brandId" element={<BrandDetailsView />} />
            </Route>
            <Route path="page/chatbots" element={<EmbedsView />}>
              <Route path=":embedId" element={<EmbedDetailsView />} />
            </Route>
            <Route path="/properties" element={<PropertiesIndexRedirect />} />
          </Route>

          {/* ─── PROPERTY MANAGER V3 ROUTES (2026-05-01) ─────────────────── */}
          {/* Mirrors the /properties block but only carries the v3 surfaces (Details v3, Cortex v3). */}
          <Route path="/properties-v3" element={isFeatureEnabled(FeatureFlagKey.Snowy) ? <RouteShowIf scopes={[APIScope.LOCATIONS_ALL_VIEW]}><PropertiesV3View /></RouteShowIf> : <Navigate to="/" replace />}>
            {/* CORTEX V3 */}
            {/* POLICY WORKSHEETS (Policy Builder, ENG-2915) — gated by its own flag */}
            <Route path="page/policy-worksheets" element={isFeatureEnabled(FeatureFlagKey.PolicyBuilder) ? <PolicyWorksheetsView /> : <Navigate to="/" replace />} />
            <Route path="group/:groupId/page/policy-worksheets" element={isFeatureEnabled(FeatureFlagKey.PolicyBuilder) ? <PolicyWorksheetsView /> : <Navigate to="/" replace />} />
            <Route path="group/:groupId/location/:locationId/page/policy-worksheets" element={isFeatureEnabled(FeatureFlagKey.PolicyBuilder) ? <PolicyWorksheetsView /> : <Navigate to="/" replace />} />
            {/* REPORTS */}
            <Route path="group/:groupId/location/:locationId/page/reports" element={<ReportsView />} />
            <Route path="group/:groupId/location/:locationId/page/reports/:folder/:dashboardId" element={<ReportsView />} />
            <Route path="group/:groupId/page/reports" element={<ReportsView />} />
            <Route path="group/:groupId/page/reports/:folder/:dashboardId" element={<ReportsView />} />
            <Route path="page/reports" element={<ReportsView />} />
            <Route path="page/reports/:folder/:dashboardId" element={<ReportsView />} />
            {/* DETAILS V3 */}
            <Route path="group/:groupId/location/:locationId/page/details-v3" element={<WorksheetLayoutView showHeader={true} hideSidebarSearch={true} data={DetailsV3WorksheetsData[LocationScope.LOCATION]} page={'details-v3'} />}>   {RouteWorksheetForData(DetailsV3WorksheetsData[LocationScope.LOCATION])}   <Route index element={<Navigate to="facility-info" replace />} />   <Route path="sequences" element={<Navigate to="../../sequences" replace />} />   <Route path="*" element={<Navigate to="facility-info" />} /> </Route>
            <Route path="group/:groupId/page/details-v3" element={<WorksheetLayoutView showHeader={true} hideSidebarSearch={true} data={DetailsV3WorksheetsData[LocationScope.GROUP]} page={'details-v3'} />}>   {RouteWorksheetForData(DetailsV3WorksheetsData[LocationScope.GROUP])}   <Route index element={<Navigate to="facility-info" replace />} />   <Route path="sequences" element={<Navigate to="../../sequences" replace />} />   <Route path="*" element={<Navigate to="facility-info" />} /> </Route>
            <Route path="page/details-v3" element={<WorksheetLayoutView showHeader={true} hideSidebarSearch={true} data={DetailsV3WorksheetsData[LocationScope.ORG]} page={'details-v3'} />}>   {RouteWorksheetForData(DetailsV3WorksheetsData[LocationScope.ORG])}   <Route index element={<Navigate to="facility-info" replace />} />   <Route path="sequences" element={<Navigate to="../../sequences" replace />} />   <Route path="*" element={<Navigate to="facility-info" />} /> </Route>
            {/* SPOT.IS — the v2 page on the Cortex spot variables (Chuck, 2026-08-30) */}
            <Route path="group/:groupId/location/:locationId/page/spot" element={<SpotV3View />} />
            <Route path="group/:groupId/page/spot" element={<SpotV3View />} />
            <Route path="page/spot" element={<SpotV3View />} />
            {/* SEQUENCES — the new sequence engine's config editor per type, org / group / location (ENG-3216).
                Behind the sequences_page flag + engine switches like the tab (ENG-3542): a deep link with it off lands on the landing page, as for Policies. */}
            <Route path="group/:groupId/location/:locationId/page/sequences" element={isSequencesConfigPageVisible() ? <RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}><SequencesV3View /></RouteShowIf> : <Navigate to="/" replace />}>   <Route path=":sequenceSlug" element={<SequenceTypePanel />} />   <Route index element={<Navigate to="past-due" replace />} /> </Route>
            <Route path="group/:groupId/page/sequences" element={isSequencesConfigPageVisible() ? <RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}><SequencesV3View /></RouteShowIf> : <Navigate to="/" replace />}>   <Route path=":sequenceSlug" element={<SequenceTypePanel />} />   <Route index element={<Navigate to="past-due" replace />} /> </Route>
            <Route path="page/sequences" element={isSequencesConfigPageVisible() ? <RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}><SequencesV3View /></RouteShowIf> : <Navigate to="/" replace />}>   <Route path=":sequenceSlug" element={<SequenceTypePanel />} />   <Route index element={<Navigate to="past-due" replace />} /> </Route>
            {/* Default landing */}
            <Route path="" element={<Navigate to="/properties-v3/page/details-v3/facility-info" replace />} />
          </Route>

          {/* ─── WIDGET BUILDER (Snowy v4, 2026-05-20) ───────────────────── */}
          {/* Top-level routes per the Figma IA: Details / CTAs Library / Install. */}
          {/* B0b ships scaffolding + placeholders. B1+ fills in editing UIs. */}
          <Route
            path="/widgets"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <WidgetsListView />
                </Suspense>
              </RouteShowIf>
            }
          />
          <Route
            path="/widgets/:widgetId"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <WidgetDetailLayout />
                </Suspense>
              </RouteShowIf>
            }
          >
            <Route
              path="branding"
              element={
                <Suspense fallback={<PageLoadingView />}>
                  <BrandingAndCtaView />
                </Suspense>
              }
            />
            <Route
              path="rules"
              element={
                <Suspense fallback={<PageLoadingView />}>
                  <DisplayRulesView />
                </Suspense>
              }
            />
            <Route path="" element={<Navigate to="branding" replace />} />
          </Route>
          <Route
            path="/ctas"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <CtasLibraryView />
                </Suspense>
              </RouteShowIf>
            }
          />
          <Route
            path="/ctas/:ctaId"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <CtaWizardView />
                </Suspense>
              </RouteShowIf>
            }
          />
          <Route
            path="/install"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <InstallView />
                </Suspense>
              </RouteShowIf>
            }
          />
          {/* ─── BRANDS (Snowy, ENG-2894) — top-level next to Widgets, same scope pair. ── */}
          <Route
            path="/brands"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <BrandsListView />
                </Suspense>
              </RouteShowIf>
            }
          />
          <Route
            path="/brands/:brandId"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_EDITOR]}>
                <Suspense fallback={<PageLoadingView />}>
                  <BrandDetailView />
                </Suspense>
              </RouteShowIf>
            }
          />
          <Route
            path="/reservations"
            element={
              <RouteShowIf scopes={[APIScope.WIDGETS_AND_BRANDS_ADMIN]}>
                <Suspense fallback={<PageLoadingView />}>
                  <ReservationsListView />
                </Suspense>
              </RouteShowIf>
            }
          />


          <Route path="inbox" element={<TicketingPage />} >
            <Route path="search" element={<TicketingPage />} />
            <Route path="ticket/:ticketNumber" element={<TicketingPage />} />

            <Route path="*" element={<Navigate to="." replace />} />

          </Route>


          <Route path="brain/:brainSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <BrainsView />       </Suspense>     </RouteShowIf>} />
          <Route path="/whats-new" element={<WhatsNewView />} />

          <Route path="/notifications" element={<NotificationsView />} />
          <Route path="/workflows" element={legacyNavHidden ? <Navigate to="/settings/analytics" replace /> : <RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>       <UserJourneysView />     </RouteShowIf>} /> <Route path="quest/:questSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>       <EditorWrapper />     </RouteShowIf>} /> <Route path="matrix/:questSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>              <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <EditorWrapper />       </Suspense>     </RouteShowIf>} /> {/* <Route path="brain/:brainSlug" element={<RouteShowIf scopes={[APIScope.WORKFLOWS_VIEW]}>  <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}><BrainView /></Suspense></RouteShowIf>} /> */}
          <Route path="fms/:id" element={<RouteShowIf scopes={[APIScope.FMS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <FMSDetailsView />       </Suspense>     </RouteShowIf>} />

          {/* Legacy /conversations routes (e.g. from emails) — redirect to /contact-center */}
          <Route path="conversations" element={<RouterRedirect to="/contact-center/conversations" replace />} />
          <Route path="conversations/all/:userId" element={<RouterRedirect to="/contact-center/conversations/user/:userId" replace />} />
          <Route path="conversations/user/:userId" element={<RouterRedirect to="/contact-center/conversations/user/:userId" replace />} />

          {/* AI Studio — internal tool: requires the org's Snowy flag AND a Swivl staff/admin
              account kind (customers never see it, even on a Snowy org; ENG-3272).
              User Stories is registered inside AIStudioView's domain-scoped
              routes and is local-dev-gated there. */}
          <Route path="ai-studio/*" element={isFeatureEnabled(FeatureFlagKey.Snowy) && Member.isSwivlStaff ? <AIStudioView /> : <Navigate to="/" replace />} />

          {/* Redirects from old routes */}
          <Route path="ai-conversations" element={<Navigate to="/ai-studio/conversations" replace />} />
          <Route path="ai-conversations/:conversationId" element={<RouterRedirect to="/ai-studio/conversations/:conversationId" replace />} />

          <Route path="contact-center/*" element={<ContactCenterView />}>
            <Route index element={<Navigate to="conversations" replace />} />
            <Route path="conversations" element={<ConversationsView />} />
            <Route path="conversations/all/:userId" element={<RouterRedirect to="/contact-center/conversations/user/:userId" />} />
            <Route path="conversations/user/:userId" element={<ConversationsView />} />


            <Route path="recordings" element={<RecordingsView />} />
          </Route>

          <Route path="contact-center-v3" element={isFeatureEnabled(FeatureFlagKey.Snowy) ? <Suspense fallback={<PageLoadingView />}><ContactCenterV3View /></Suspense> : <Navigate to="/" replace />}>
            <Route index element={<Navigate to="conversations" replace />} />
            <Route path="conversations" element={<Suspense fallback={<PageLoadingView />}><ConversationsV3Page /></Suspense>} />
            <Route path="conversations/:userId" element={<Suspense fallback={<PageLoadingView />}><ConversationsV3Page /></Suspense>} />
            <Route path="agent-dashboard" element={<Suspense fallback={<PageLoadingView />}><AgentDashboardPage /></Suspense>} />
            <Route path="agent-dashboard/tv" element={<Suspense fallback={<PageLoadingView />}><AgentDashboardTVModePage /></Suspense>} />
            {/* The Recordings page is retired (ENG-3814): a bookmark lands on Conversations. */}
            <Route path="recordings" element={<Navigate to="/contact-center-v3/conversations" replace />} />
          </Route>

          <Route path="reviews" element={<RouteShowIf scopes={[APIScope.REVIEWS_EDIT]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <ReviewsView />       </Suspense>     </RouteShowIf>} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          {/* The nav's Pulse item. */}
          <Route path="/pulse" element={isPulseEnabled() ? <Suspense fallback={<PageLoadingView />}><PulsePage /></Suspense> : <Navigate to="/" replace />} />
          <Route path="/" element={<DefaultLandingRedirect />} />

          {/* old crm */}
          <Route path="analytics/crm" element={<RouteShowIf scopes={[APIScope.USER_CRM_VIEW]}><OldCRMView /></RouteShowIf>} />
          <Route path="analytics/crm/:startDate/:endDate" element={<RouteShowIf scopes={[APIScope.USER_CRM_VIEW]}><OldCRMView /></RouteShowIf>} />

          {/* crm */}
          <Route path="crm" element={<RouteShowIf scopes={[APIScope.USER_CRM_VIEW]}><CRMView /></RouteShowIf>} />
          {/* Snowy-only CRM pages — a legacy org is sent back to Users, deep link or not (ENG-3475).
              The two sequence pages also need the sequences_page flag (ENG-3542), same as their tabs. */}
          <Route path="crm/sequence-events" element={isFeatureEnabled(FeatureFlagKey.Snowy) && isFeatureEnabled(FeatureFlagKey.SequencesPage) ? <RouteShowIf scopes={[APIScope.USER_CRM_VIEW]}><SequenceEventsView /></RouteShowIf> : <Navigate to="/crm" replace />} />
          {/* The Sequence Suppressions page was absorbed into Sequences (ENG-3811): its deep links
              keep their filters and land on the Suppressions tab. */}
          <Route path="crm/sequence-suppressions" element={<SequenceSuppressionsRedirect />} />
          <Route path="crm/sms-campaigns" element={isFeatureEnabled(FeatureFlagKey.Snowy) ? <RouteShowIf scopes={[APIScope.USER_CRM_VIEW]}><SmsCampaignsView /></RouteShowIf> : <Navigate to="/crm" replace />} />

          <Route path="transcript/:userId" element={<TranscriptView />} />
          <Route path="/settings/sequences/:sequenceId/record/:recordId" element={legacyNavHidden ? <Navigate to="/settings/analytics" replace /> : <RouteShowIf scopes={[APIScope.SEQUENCES_VIEW]}>       <Suspense fallback={<PageLoadingView navBarTitle=" " pageTitle=" " />}>         <SequenceRecordDetails />       </Suspense>      </RouteShowIf>} />

          {/* Short URL for tickets - redirects to inbox view */}
          <Route path="ticket/:ticketNumber" element={<RouterRedirect to="/inbox/ticket/:ticketNumber" />} />
        </Route>

        {/* No nav chrome, even when logged in — the anonymous ticket link. */}
        <Route path="ticket-access/:token" element={<TicketAccessView />} />
        <Route path="conversations/access/:token" element={<TranscriptAccessView />} />

        <Route path="/*" element={<Navigate to="/" />} />

        <Route path="/404" element={<ErrorView />} />
      </Routes>
    </>
  );
}
